/**
 * « Dans la cible » — **une seule définition**, des deux côtés de l'API.
 *
 * 🔴 **Quatrième fichier de `shared/`, et c'est la justification de
 * `shared/search.ts` mot pour mot.** Le classement d'une recherche devait être
 * le même des deux côtés « parce que deux implémentations divergeraient, et que
 * le symptôme serait la même saisie rendant les mêmes résultats dans un autre
 * ordre selon la connexion ». Ici c'est la tolérance de ±5 % : elle colore déjà
 * le bandeau du Journal (`web/src/lib/macroStatus.ts`) et elle décide, depuis
 * l'étape 9 de la V2, du « % de jours dans la cible » calculé **côté serveur**.
 * Deux valeurs, et le tableau de bord annoncerait 71 % de jours atteints sur
 * des journées que le Journal a peintes en vert — un défaut que personne ne
 * signale, on se dit qu'on a mal compté.
 *
 * C'est aussi ce que l'ARCHITECTURE_V2 §6.2 exige nommément : la métrique est
 * retenue « avec la tolérance ±5 % de `lib/macroStatus.ts` — celle qui colore
 * déjà l'écran. Une seconde définition de "dans la cible" ferait diverger le
 * tableau de bord de l'écran Journal ».
 *
 * ⚠️ **Un fichier à part, et non une ligne de plus dans `types.ts`.** Ce
 * dernier s'interdit explicitement toute logique de calcul et ne tolère
 * `roundMacroForDisplay` que comme une brèche « à ne pas élargir ». On ne
 * l'élargit donc pas : on rouvre à côté, avec son propre motif, exactement
 * comme `syncable.ts` et `search.ts` avant lui.
 *
 * ⚠️ **Aucun jugement ici.** `onTarget` dit « atteint », jamais « bien »
 * (specs §6.2) — le fichier ne produit qu'un booléen, et c'est l'écran qui
 * décide ce qu'il en fait.
 */

import { roundMacroForDisplay } from './types.js';

/**
 * Largeur de la bande considérée comme « atteint », en **part de l'objectif**.
 *
 * ⚠️ Un pourcentage, jamais une valeur absolue : ±5 % de 2 465 kcal fait
 * ±123 kcal, ±5 % de 40 g de lipides fait ±2 g. La même marge en grammes
 * n'aurait aucun sens d'une macro à l'autre.
 */
export const ON_TARGET_TOLERANCE = 0.05;

/**
 * Le restant est-il « dans la cible » ?
 *
 * 🔴 **Le jugement porte sur la valeur ARRONDIE, celle qui est affichée.**
 * C'est la règle que `macroStatus.ts` tient depuis la refonte, et elle vaut
 * autant pour un comptage que pour une couleur : un restant de −0,04 g
 * s'affiche « 0 ». Le compter comme un dépassement ferait dire au tableau de
 * bord qu'une journée a raté sa cible alors que le Journal l'a montrée
 * atteinte, sur une différence que ni l'un ni l'autre n'écrit.
 *
 * @param remaining restant brut (négatif si dépassé)
 * @param target objectif ; `0` ou moins signifie « pas d'objectif »
 * @param isKcal choisit l'arrondi (kcal entière, gramme au dixième)
 * @returns `null` quand il n'y a pas d'objectif — ni atteint, ni raté. Un
 *   booléen forcerait à ranger ce cas d'un côté ou de l'autre, et les deux
 *   seraient faux : une journée sans objectif n'est pas une journée manquée,
 *   elle ne compte pas. C'est la discipline du `null` de `weeklyTrend()`.
 */
export function isOnTarget(remaining: number, target: number, isKcal: boolean): boolean | null {
  if (target <= 0) return null;
  const displayed = roundMacroForDisplay(remaining, isKcal);
  return Math.abs(displayed) <= target * ON_TARGET_TOLERANCE;
}
